import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Building2, CheckCircle2, CircleDollarSign,
  FileText, Globe2, Layers, Lock, Sparkles, TrendingUp, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '/books/pricing' },
  { label: 'For accountants', href: '#accountants' },
  { label: 'Docs', href: '#docs' },
];

const FEATURES = [
  {
    icon: BarChart3,
    title: 'Double-entry general ledger',
    desc: 'Every transaction posts a balanced journal. Audit-ready by design.',
  },
  {
    icon: FileText,
    title: 'Invoices, bills & quotes',
    desc: 'Send branded PDFs, chase overdue, convert quotes in one click.',
  },
  {
    icon: Globe2,
    title: 'Multi-currency',
    desc: 'Auto-fetch FX rates, book realised & unrealised gains automatically.',
  },
  {
    icon: Layers,
    title: 'Inventory & stock',
    desc: 'Track SKUs, on-hand quantities, weighted-average COGS.',
  },
  {
    icon: TrendingUp,
    title: 'Real-time reports',
    desc: 'Trial balance, P&L, balance sheet, cash flow — always current.',
  },
  {
    icon: Building2,
    title: 'Multi-branch',
    desc: 'Consolidate across offices with cost-centre tagging.',
  },
  {
    icon: CircleDollarSign,
    title: 'Payroll',
    desc: 'Run salary cycles with UK PAYE, India PF/EPF, tax withholding.',
  },
  {
    icon: Zap,
    title: 'Public API + webhooks',
    desc: 'Push invoices from any system. Get notified on every event.',
  },
];

const COUNTRIES = [
  { code: 'GB', name: 'UK', tax: 'VAT 9-box (HMRC)' },
  { code: 'IN', name: 'India', tax: 'GSTR-1, GSTR-3B' },
  { code: 'MY', name: 'Malaysia', tax: 'SST 10% / 8%' },
  { code: 'KZ', name: 'Kazakhstan', tax: 'VAT return' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [logged, setLogged] = useState(false);
  useEffect(() => {
    setLogged(!!localStorage.getItem('books_token'));
  }, []);

  return (
    <div className="min-h-screen bg-[#050b12] text-slate-100 antialiased" data-testid="books-landing-page">
      {/* Gradient orbs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-emerald-500/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-400/10 blur-[100px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-20 border-b border-white/5 bg-[#050b12]/70 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/books" className="flex items-center gap-3" data-testid="books-nav-logo">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 font-bold text-white shadow-lg shadow-emerald-500/30">
              EB
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight">Emerald Books</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400/80">Cloud accounting</div>
            </div>
          </Link>
          <div className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="text-sm text-slate-300 transition hover:text-white">
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {logged ? (
              <Button
                onClick={() => navigate('/books/app/dashboard')}
                className="rounded-full bg-emerald-500 px-5 text-white hover:bg-emerald-400"
                data-testid="books-nav-dashboard"
              >
                Go to dashboard <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <>
                <Link to="/books/login" className="hidden text-sm text-slate-300 hover:text-white sm:inline" data-testid="books-nav-login">
                  Log in
                </Link>
                <Button
                  onClick={() => navigate('/books/signup')}
                  className="rounded-full bg-emerald-500 px-5 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-400"
                  data-testid="books-nav-signup"
                >
                  Start free
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pt-20 pb-24 md:pt-32">
        <div className="max-w-4xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> 14-day free trial · no card required
          </div>
          <h1 className="text-5xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Accounting software your{' '}
            <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-cyan-300 bg-clip-text text-transparent">
              finance team
            </span>{' '}
            will love.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 sm:text-xl">
            Double-entry ledger, multi-currency, inventory, payroll, tax returns
            and open APIs — in one clean workspace. Built for teams operating in
            the UK, India, Malaysia and Kazakhstan.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              onClick={() => navigate('/books/signup')}
              className="group h-12 rounded-full bg-emerald-500 px-7 text-base font-semibold text-white shadow-xl shadow-emerald-500/30 transition hover:bg-emerald-400 hover:shadow-emerald-400/40"
              data-testid="books-hero-cta-signup"
            >
              Start free trial
              <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
            </Button>
            <Link
              to="/books/pricing"
              className="text-base text-slate-200 underline-offset-4 hover:text-white hover:underline"
              data-testid="books-hero-cta-pricing"
            >
              See pricing →
            </Link>
          </div>
          <div className="mt-14 flex flex-wrap items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-emerald-400" /> Bank-grade encryption</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> GDPR-ready</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> SOC 2 roadmap</span>
          </div>
        </div>

        {/* Product screenshot mock */}
        <div className="mt-20 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-2 shadow-2xl">
          <div className="rounded-xl bg-[#0a1420] p-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
              <div className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
              <span className="ml-2 text-xs text-slate-500">books.example.com/dashboard</span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Cash on hand', value: '£184,320', trend: '+12.4%' },
                { label: 'Outstanding AR', value: '£42,110', trend: '−6.1%' },
                { label: 'MRR', value: '£18,900', trend: '+8.2%' },
                { label: 'Net profit YTD', value: '£62,780', trend: '+22%' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{s.label}</div>
                  <div className="mt-1 text-2xl font-semibold text-white">{s.value}</div>
                  <div className="mt-1 text-xs text-emerald-400">{s.trend}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 h-40 rounded-lg border border-white/5 bg-gradient-to-r from-emerald-500/10 via-cyan-500/5 to-transparent" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 mx-auto max-w-7xl px-6 py-24">
        <div className="mb-14 max-w-2xl">
          <div className="text-sm font-medium uppercase tracking-widest text-emerald-400">The complete stack</div>
          <h2 className="mt-3 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Everything a modern finance team needs.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="group rounded-2xl border border-white/5 bg-white/[0.02] p-6 transition hover:border-emerald-400/20 hover:bg-white/[0.04]"
                data-testid={`books-feature-${f.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-base font-semibold text-white">{f.title}</div>
                <div className="mt-2 text-sm text-slate-400">{f.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Countries */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 py-24">
        <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-10">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <div className="text-sm font-medium uppercase tracking-widest text-emerald-400">Built for your jurisdiction</div>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Ready-to-file tax returns.
              </h2>
              <p className="mt-4 text-slate-300">
                We ship native tax return summaries for the UK, India, Malaysia
                and Kazakhstan — auto-populated from your ledger. More
                jurisdictions on request.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {COUNTRIES.map((c) => (
                <div
                  key={c.code}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
                  data-testid={`books-country-${c.code.toLowerCase()}`}
                >
                  <div className="text-xs uppercase tracking-wider text-slate-500">{c.code}</div>
                  <div className="mt-1 text-lg font-semibold text-white">{c.name}</div>
                  <div className="mt-1 text-sm text-emerald-300/80">{c.tax}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-24">
        <div className="overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent p-12 text-center">
          <h2 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Start closing books faster this month.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">
            14-day free trial. No credit card. Cancel anytime — your data is
            yours to export.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Button
              onClick={() => navigate('/books/signup')}
              className="h-12 rounded-full bg-emerald-500 px-8 text-base font-semibold text-white shadow-xl shadow-emerald-500/30 hover:bg-emerald-400"
              data-testid="books-cta-signup-bottom"
            >
              Create your workspace <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 text-xs text-slate-500 sm:flex-row">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 text-[10px] font-bold text-white">EB</div>
            <span>Emerald Books · Cloud accounting</span>
          </div>
          <div className="flex gap-5">
            <Link to="/books/pricing" className="hover:text-white">Pricing</Link>
            <a href="#docs" className="hover:text-white">Docs</a>
            <Link to="/books/login" className="hover:text-white">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
